import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Alert, AlertTitle, RadioGroup, FormControlLabel, Radio,
  CircularProgress, Box, alpha, useTheme
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { promotionsApi } from '../../services/sessions/promotionsApi';

interface FinalizeRunDialogProps {
  open: boolean;
  runId: string;
  /** How many source enrollments are NOT yet committed (will be the leftovers). */
  remaining: number;
  totalInScope: number;
  totalCommitted: number;
  onClose: () => void;
  onFinalized: () => void;
}

type Disposition = 'inconclusive' | 'promote' | 'retain' | 'drop';

const FinalizeRunDialog: React.FC<FinalizeRunDialogProps> = ({
  open, runId, remaining, totalInScope, totalCommitted, onClose, onFinalized
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [disposition, setDisposition] = useState<Disposition>('inconclusive');
  const [submitting, setSubmitting]   = useState(false);

  useEffect(() => { if (!open) setDisposition('inconclusive'); }, [open]);

  const hasLeftovers = remaining > 0;

  const submit = async () => {
    setSubmitting(true);
    try {
      await promotionsApi.finalize(runId, hasLeftovers ? { leftoverDisposition: disposition } : {});
      enqueueSnackbar('Run finalized', { variant: 'success' });
      onFinalized();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not finalize', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Lock color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Finalize this run</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Closes the promotion run. Once finalized, no further chunks can be committed.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{
          mb: 2, p: 1.5, borderRadius: 2,
          bgcolor: alpha(theme.palette.text.primary, 0.04)
        }}>
          <Stack direction="row" spacing={3}>
            <Stat label="In scope"  value={totalInScope} />
            <Stat label="Committed" value={totalCommitted} color="success.dark" />
            <Stat label="Remaining" value={remaining} color={remaining > 0 ? 'warning.dark' : 'text.secondary'} />
          </Stack>
        </Box>

        {hasLeftovers ? (
          <Stack spacing={2}>
            <Alert severity="warning" variant="outlined">
              <AlertTitle sx={{ fontWeight: 600 }}>{remaining} student{remaining === 1 ? '' : 's'} still need a decision</AlertTitle>
              <Typography variant="body2" color="text.secondary">
                Pick a fallback below — it applies in one shot to every uncommitted student.
                You can revisit any of them later from the Enrollments page.
              </Typography>
            </Alert>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Fallback disposition</Typography>
              <RadioGroup value={disposition} onChange={(_, v) => setDisposition(v as Disposition)}>
                <FormControlLabel
                  value="inconclusive"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Mark as inconclusive (decide later)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Safest default — closes the source row without creating a target. You can revisit each student
                        from the Enrollments page filtered by status = Inconclusive.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="promote"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Promote them anyway</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Move every leftover student to the next class. Use when all results are confirmed pass.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="retain"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Retain them</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Keep every leftover student in the same class for the new session.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="drop"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Drop them</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Close as dropped out. Use cautiously.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>
          </Stack>
        ) : (
          <Alert severity="success" variant="outlined">
            <AlertTitle sx={{ fontWeight: 600 }}>Every student has been processed</AlertTitle>
            <Typography variant="body2" color="text.secondary">
              Ready to close the run. You'll be able to view the audit trail anytime from the Sessions page.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          color={hasLeftovers ? 'warning' : 'primary'}
          disabled={submitting}
          onClick={submit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Finalizing…' : hasLeftovers ? 'Apply & finalize' : 'Finalize run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Stat: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Typography variant="h6" fontWeight={700} sx={{ color: color ?? 'text.primary', lineHeight: 1.1 }}>{value}</Typography>
  </Box>
);

export default FinalizeRunDialog;
